import type { Timestamp } from 'firebase/firestore'
import type { RichTextContent } from '@/types'

/**
 * Extracts a plain-text preview from a TipTap JSON RichTextContent.
 * Recursively walks the node tree collecting `text` leaf values.
 *
 * @param content  The RichTextContent to extract text from.
 * @param maxLen   Maximum characters to return (default 120).
 */
export function richTextToPlainText(
  content: RichTextContent | null | undefined,
  maxLen = 120
): string {
  if (!content?.data) return ''
  const text = extractText(content.data as TipTapNode)
  const trimmed = text.trim().replace(/\s+/g, ' ')
  return trimmed.length > maxLen ? `${trimmed.slice(0, maxLen)}…` : trimmed
}

interface TipTapNode {
  type?: string
  text?: string
  content?: TipTapNode[]
}

function extractText(node: TipTapNode): string {
  if (node.text) return node.text
  if (!node.content) return ''
  return node.content.map(extractText).join(' ')
}

/**
 * Formats a Firestore Timestamp into a human-readable date/time string.
 *
 * @example
 * formatTimestamp(ts) // "Jan 5, 2026 at 3:00 PM"
 */
export function formatTimestamp(timestamp: Timestamp): string {
  return timestamp.toDate().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Formats a duration in seconds into a compact human-readable string.
 *
 * @example
 * formatDuration(5400) // "1h 30m"
 * formatDuration(2720) // "45m 20s"
 * formatDuration(30)   // "30s"
 */
export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60

  if (h > 0) return `${h}h${m > 0 ? ` ${m}m` : ''}`
  if (m > 0) return `${m}m${s > 0 ? ` ${s}s` : ''}`
  return `${s}s`
}

/**
 * Formats a score fraction as a readable string with percentage.
 *
 * @example
 * formatScore(8, 10) // "8 / 10 (80%)"
 */
export function formatScore(score: number, total: number): string {
  const pct = total > 0 ? Math.round((score / total) * 100) : 0
  return `${score} / ${total} (${pct}%)`
}

/**
 * Truncates a string to a maximum length, appending "…" if truncated.
 *
 * @param text - The string to truncate.
 * @param maxLength - Maximum number of characters before truncation.
 * @returns The (possibly truncated) string.
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '…'
}
